import * as React from 'react';
import { Enum } from 'typescript-string-enums';
import { IPaymentDetails, IUserInfo } from '../CustomTypes';
import { strexMethods, strexPaymentMethod } from '../actions/Actions';
import {
  Loader,
  Button,
  classNames,
  Padding,
  Display,
  JustifyContent,
  BorderColor,
  BorderWidth,
  BorderStyle,
  Margin,
  TextColor,
  TextAlign,
  FlexDirection,
  AlignItems,
  Width,
  FontSize,
  RadioButton,
  BackgroundColor,
  LoaderSize,
  LoaderColor,
  Position,
  ZIndex,
} from '@snoam/pinata';
import InputField from '../input-components/InputField';
import StrexAdditionalInformation from './StrexAdditionalInformation';
import { PropsWithChildren } from 'react';

const steps = Enum(
  'START',
  'REGISTERED_AND_VALIDATED',
  'ADDITIONAL_INFORMATION',
);

interface IStrexPaymentMethodProps {
  callCreateOrder: (paymentId: string) => void;
  paymentDetails: IPaymentDetails;
  selectedPaymentMethodId: number;
  userInfo: IUserInfo;
  price: number;
  disclaimer?: JSX.Element;
}

interface IStrexPaymentMethodState {
  code: string;
  error: string;
  loading: boolean;
  paymentId: string;
  phone: string;
  step: string;
  strexWidgetUrl: string;
  blockLoading: boolean;
}

const styleClass = {
  container: classNames(
    BorderColor.BORDER_NEUTRAL_2,
    BorderWidth.BORDER_B_1,
    BorderStyle.BORDER_SOLID,
  ),
  text: classNames(
    Display.FLEX,
    JustifyContent.JUSTIFY_BETWEEN,
    Padding.PT_4,
    Padding.PB_2,
    BackgroundColor.BG_WHITE,
    AlignItems.ITEMS_CENTER,
  ),
  radioButton: classNames(Margin.MR_4, Margin.MT_1),
  warningText: classNames(
    TextColor.TEXT_WARNING,
    TextAlign.TEXT_CENTER,
    Margin.MB_2,
  ),
  strexInput: classNames(
    Display.FLEX,
    FlexDirection.FLEX_COL,
    AlignItems.ITEMS_CENTER,
    Width.W_FULL,
  ),
  loader: classNames(Display.FLEX, JustifyContent.JUSTIFY_CENTER, Margin.MY_5),
  blockLoader: classNames(Position.ABSOLUTE, ZIndex.Z_20),
  root: classNames(
    Position.FIXED,
    Position.TOP_0,
    Position.BOTTOM_0,
    Position.LEFT_0,
    Position.RIGHT_0,
    BackgroundColor.BG_NEUTRAL_5,
    ZIndex.Z_10,
  ),
};

class StrexPaymentMethod extends React.Component<
  PropsWithChildren<IStrexPaymentMethodProps>,
  IStrexPaymentMethodState
> {
  public static displayName = 'StrexPaymentMethod';

  constructor(props: PropsWithChildren<IStrexPaymentMethodProps>) {
    super(props);
    this.handleCodeChange = this.handleCodeChange.bind(this);
    this.handlePhoneChange = this.handlePhoneChange.bind(this);
    this.createStrexPaymentMethod = this.createStrexPaymentMethod.bind(this);
    this.renderStrexStep = this.renderStrexStep.bind(this);
    this.verifyStrexPaymentMethod = this.verifyStrexPaymentMethod.bind(this);
    this.handleCreateOrder = this.handleCreateOrder.bind(this);
    this.state = {
      blockLoading: false,
      code: '',
      error: '',
      loading: false,
      paymentId: '',
      phone: this.props.userInfo.phoneNumber || '',
      step: steps.START,
      strexWidgetUrl: '',
    };
  }

  public componentDidUpdate(
    prevProps: Readonly<IStrexPaymentMethodProps>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    prevState: Readonly<IStrexPaymentMethodState>,
  ) {
    if (prevProps.userInfo.phoneNumber !== this.props.userInfo.phoneNumber) {
      this.setState({
        phone: this.props.userInfo.phoneNumber,
      });
    }
  }

  public render() {
    const { paymentDetails, selectedPaymentMethodId, disclaimer } = this.props;
    const selected = selectedPaymentMethodId === paymentDetails.id;
    return (
      <div key={paymentDetails.id} className={styleClass.container}>
        <label htmlFor={`payment-method-${paymentDetails.id}`}>
          <div className={styleClass.text}>
            <div>
              <RadioButton
                id={`payment-method-${paymentDetails.id}`}
                value={'' + paymentDetails.id}
              />
            </div>
            {this.props.children}
          </div>
        </label>
        {selected && (
          <>
            {disclaimer}
            <div className={styleClass.warningText}>{this.state.error}</div>
            {this.renderStrexStep()}
          </>
        )}
      </div>
    );
  }

  private renderStrexStep() {
    let child;
    const centeredLoader = (
      <div className={styleClass.loader}>
        <Loader text={'Laster'} size={LoaderSize.SMALL} />
      </div>
    );

    switch (this.state.step) {
      case steps.START: {
        child = (
          <div className={styleClass.strexInput}>
            <InputField
              value={this.state.phone}
              onChange={this.handlePhoneChange}
              onEnter={this.createStrexPaymentMethod}
              initialFocus={true}
              label="Telefonnummer"
            />
            {this.state.loading ? (
              centeredLoader
            ) : (
              <Button
                onClick={this.createStrexPaymentMethod}
                onKeyDown={this.createStrexPaymentMethodHandler}
                disabled={
                  this.state.phone === undefined || this.state.phone.length < 8
                }
                text={'Registrer mobilnummer'}
              />
            )}
          </div>
        );
        break;
      }
      case steps.REGISTERED_AND_VALIDATED: {
        child = (
          <div className={styleClass.strexInput}>
            <span className={FontSize.TEXT_LG}>{this.state.phone}</span>
            <InputField
              value={this.state.code}
              onChange={this.handleCodeChange}
              onEnter={this.verifyStrexPaymentMethod}
              initialFocus={true}
              label="Kode"
            />
            {this.state.loading ? (
              centeredLoader
            ) : (
              <Button
                onClick={this.verifyStrexPaymentMethod}
                onKeyDown={this.verifyStrexPaymentMethodHandler}
                disabled={
                  this.state.code === undefined || this.state.code.length < 4
                }
                text={'Fullfør bestillingen'}
              />
            )}
          </div>
        );
        break;
      }
      case steps.ADDITIONAL_INFORMATION: {
        child = (
          <StrexAdditionalInformation
            strexWidgetUrl={this.state.strexWidgetUrl}
            phoneNumber={this.state.phone}
            toggleBlockLoading={this.toggleBlockLoading}
            verifyStrexPaymentMethod={this.verifyStrexPaymentMethod}
          />
        );
      }
    }
    return (
      <>
        {this.state.blockLoading && (
          <div className={styleClass.root} style={{ opacity: '0.6' }}>
            <div
              className={styleClass.blockLoader}
              style={{
                top: '50%',
                left: '50%',
                transform: 'translate(-50% , -50%)',
              }}
            >
              <Loader
                text={''}
                size={LoaderSize.MEDIUM}
                color={LoaderColor.WHITE}
              />
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center' }}>{child}</div>
      </>
    );
  }

  private createStrexPaymentMethodHandler = (
    e: React.KeyboardEvent<HTMLAnchorElement>,
  ) => {
    if (e.key === 'Enter') {
      this.createStrexPaymentMethod();
    }
  };

  private verifyStrexPaymentMethodHandler = (
    e: React.KeyboardEvent<HTMLAnchorElement>,
  ) => {
    if (e.key === 'Enter') {
      this.verifyStrexPaymentMethod();
    }
  };

  private toggleBlockLoading = (value: boolean) => {
    this.setState({ blockLoading: value });
  };

  private handleCreateOrder(paymentId) {
    this.setState({ loading: true });
    this.props.callCreateOrder(paymentId);
  }

  private handleCodeChange(event) {
    const code = event.target.value;
    this.setState({ code });
  }

  private verifyStrexPaymentMethod() {
    if (this.state.code.length >= 4) {
      this.setState({ loading: true });
      strexPaymentMethod(
        this.props.userInfo.spidId,
        this.props.userInfo.sig,
        this.state.paymentId,
        strexMethods.validate,
        this.state.code,
        this.props.price,
      )
        .then((res) => {
          if (res.status === 303) {
            this.setState({
              blockLoading: false,
              error: undefined,
              loading: false,
              step: steps.ADDITIONAL_INFORMATION,
            });
          }
          return res.json();
        })
        .then((data: any) => {
          if (data && data.paymentId) {
            this.setState({
              error: undefined,
              paymentId: data.paymentId,
            });
            this.handleCreateOrder(data.paymentId);
          } else if (data && data.widgetUrl) {
            this.setState({
              blockLoading: false,
              strexWidgetUrl: data.widgetUrl,
            });
          } else if (data.error === 'Cannot use STREX') {
            this.setState({
              blockLoading: false,
              error:
                'Du kan dessverre ikke bruke dette telefonnummeret til STREX-betaling. Velg et annet betalingsalternativ.',
              loading: false,
            });
          } else {
            this.setState({
              blockLoading: false,
              error: 'Feil under validering av koden',
              loading: false,
            });
          }
        });
    }
  }

  private handlePhoneChange(event) {
    const phone = event.target.value;
    this.setState({ phone });
  }

  private createStrexPaymentMethod() {
    if (this.state.phone && this.state.phone.length >= 8) {
      if (isNaN(Number(this.state.phone))) {
        this.setState({ error: 'Ikke gyldig telefonnummer' });
        return;
      }
      this.setState({ loading: true });
      strexPaymentMethod(
        this.props.userInfo.spidId,
        this.props.userInfo.sig,
        '47' + this.state.phone,
        strexMethods.create,
      )
        .then((res) => res.json())
        .then((data: any) => {
          if (data && data.paymentId) {
            this.setState({
              error: undefined,
              loading: false,
              paymentId: data.paymentId,
              step: steps.REGISTERED_AND_VALIDATED,
            });
          } else {
            this.setState({
              error: 'Feil under opprettelsen av betalingsmetoden',
              loading: false,
            });
          }
        });
    }
  }
}

export default StrexPaymentMethod;
